import React, { FC } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { loginWithEmail } from '../../services/authentication/AuthenticationService';
import { removeOrganizationId, setKreezeeSession } from '../../utilities/LocalStorage';
import { FormattedMessage, useIntl } from 'react-intl';
import { Spinner } from 'react-bootstrap';
import { history } from '../../history';
import { setCurrent } from '../../slices/user';

interface Props {
  setError: (e: string) => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  invitationId?: string | undefined;
}

const KreezeeLogin: FC<Props> = ({ setError, loading, setLoading, invitationId }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'fields.fieldErrors.email' }))
      .required(),
    password: Yup.string().min(4).required(),
  });

  function handleLogin(email, password) {
    setLoading(true);
    setError('');
    loginWithEmail(email, password, invitationId)
      .then((response) => {
        if (response.data) {
          if (response.data.status == 4) {
            setError(intl.formatMessage({ id: 'loginPage.awaitingActivation' }));
            setLoading(false);
            return;
          }

          removeOrganizationId();
          setKreezeeSession(JSON.stringify({ jwt: response.data.jwt }));
          dispatch(setCurrent({ ...response.data, jwt: null }));
          history.push('/');
        }
      })
      .catch((e) => {
        if (e.response.status === 401) {
          setError(intl.formatMessage({ id: 'loginPage.wrongCombination' }));
        } else {
          setError(intl.formatMessage({ id: 'errors.generic' }));
        }
        setLoading(false);
      });
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={LoginSchema}
        onSubmit={(values) => handleLogin(values.email, values.password)}
      >
        {({ errors, touched, values, handleChange, handleBlur, handleSubmit }) => (
          <Form className="kreezee-login" noValidate onSubmit={handleSubmit}>
            <div className="form-group">
              <Form.Label>{intl.formatMessage({ id: 'loginPage.email' })}</Form.Label>
              <Form.Control
                disabled={loading}
                type="email"
                name="email"
                placeholder={intl.formatMessage({ id: 'loginPage.email' })}
                isInvalid={touched.email && !!errors.email}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                style={{ background: 'white' }}
              />
              <Form.Control.Feedback className="text-center" type="invalid">
                *{errors.email && <FormattedMessage id="loginPage.emailError"></FormattedMessage>}
              </Form.Control.Feedback>
            </div>
            <div className="form-group">
              <Form.Label>{intl.formatMessage({ id: 'loginPage.password' })}</Form.Label>
              <Form.Control
                disabled={loading}
                type="password"
                name="password"
                placeholder={intl.formatMessage({ id: 'loginPage.password' })}
                isInvalid={touched.password && !!errors.password}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
              <Form.Control.Feedback className="text-center" type="invalid">
                *{errors.password && <FormattedMessage id="loginPage.passwordError"></FormattedMessage>}
              </Form.Control.Feedback>
            </div>

            <Button disabled={loading} variant="primary" type="submit" className="submit-button">
              {!loading && (
                <div className="d-flex justify-content-center align-items-center">
                  {intl.formatMessage({ id: 'loginPage.login' })}
                </div>
              )}
              {loading && <Spinner size="sm" animation="border" />}
            </Button>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default KreezeeLogin;
